import React, { useCallback, useState, useEffect, useRef } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import {
  Bold, Italic, Underline as UnderlineIcon,
  AlignLeft, AlignCenter, AlignRight, Minus,
  List, ListOrdered, Palette, Sparkles, Code2
} from "lucide-react";

const STORAGE_KEY = "tideshub_recent_colors";
const MAX_RECENT = 8;

const PRESET_COLORS = [
  // Row 1 - Darks & Neutrals
  "#000000", "#1f2937", "#374151", "#6b7280", "#9ca3af",
  // Row 2 - Brand & Blues
  "#0F4C81", "#00A3E0", "#1e40af", "#2563eb", "#60a5fa",
  // Row 3 - Reds & Oranges
  "#991b1b", "#dc2626", "#ef4444", "#ea580c", "#f97316",
  // Row 4 - Greens & Yellows
  "#166534", "#16a34a", "#22c55e", "#ca8a04", "#eab308",
  // Row 5 - Purples & Pinks
  "#5b21b6", "#7c3aed", "#a855f7", "#9d174d", "#ec4899",
];

function getRecentColors() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch { return []; }
}

function saveRecentColor(color) {
  try {
    const recent = getRecentColors().filter(c => c !== color);
    recent.unshift(color);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(recent.slice(0, MAX_RECENT)));
  } catch {}
}

// Converts plain-text-bullet paragraphs (`* foo`, `- foo`, `• foo`, `1. foo`, `1) foo`) into
// proper <ul>/<ol><li> lists while preserving inline formatting (bold, color, etc.).
// Also handles the case where multiple bullet lines live in a single <p> separated by <br>.
const BULLET_RE = /^\s*(?:[*\-•·●◦▪▫►▸◆]|\u2022)\s+/;
const NUMBER_RE = /^\s*(?:\(?\d{1,3}\)?[.)])\s+/;

// Split a <p> by <br> elements into groups of inline nodes (one group per "line").
function splitParagraphByBr(p, doc) {
  const groups = [[]];
  Array.from(p.childNodes).forEach((node) => {
    if (node.nodeType === 1 && node.tagName === "BR") {
      groups.push([]);
    } else {
      groups[groups.length - 1].push(node.cloneNode(true));
    }
  });
  // Convert each group into a fresh <p>-like fragment (just a DocumentFragment)
  return groups.map((g) => {
    const frag = doc.createDocumentFragment();
    g.forEach((n) => frag.appendChild(n));
    return frag;
  });
}

function fragmentText(frag) {
  // Quick textContent of a fragment
  const tmp = document.createElement("div");
  tmp.appendChild(frag.cloneNode(true));
  return (tmp.textContent || "").trim();
}

function autoBulletHtml(html) {
  if (!html || typeof html !== "string") return null;
  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div id="__root">${html}</div>`, "text/html");
  const root = doc.getElementById("__root");
  if (!root) return null;

  // Step 1: expand any <p> that contains <br>-separated lines all matching a bullet/number
  // pattern into individual <p> children.
  Array.from(root.children).forEach((p) => {
    if (p.tagName !== "P") return;
    if (!p.querySelector("br")) return;
    const groups = splitParagraphByBr(p, doc);
    const anyBullet = groups.some((g) => BULLET_RE.test(fragmentText(g)));
    const anyNumber = groups.some((g) => NUMBER_RE.test(fragmentText(g)));
    if (!anyBullet && !anyNumber) return;
    // Replace p with a sequence of fresh <p> per group
    const replacement = doc.createDocumentFragment();
    groups.forEach((g) => {
      if (fragmentText(g).length === 0) return;
      const np = doc.createElement("p");
      np.appendChild(g);
      replacement.appendChild(np);
    });
    p.parentNode.replaceChild(replacement, p);
  });

  // Step 2: group consecutive bullet/number <p>s into <ul>/<ol>.
  const children = Array.from(root.children);
  const getText = (el) => (el.textContent || "").trim();
  const isBulletP = (el) => el.tagName === "P" && BULLET_RE.test(getText(el));
  const isNumberP = (el) => el.tagName === "P" && NUMBER_RE.test(getText(el));

  const stripPrefix = (el, re) => {
    const walker = doc.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    const first = walker.nextNode();
    if (first) first.nodeValue = first.nodeValue.replace(re, "");
  };

  const frag = doc.createDocumentFragment();
  let i = 0;
  let changed = false;
  while (i < children.length) {
    const el = children[i];
    if (isBulletP(el)) {
      const ul = doc.createElement("ul");
      while (i < children.length && isBulletP(children[i])) {
        const p = children[i];
        stripPrefix(p, BULLET_RE);
        const li = doc.createElement("li");
        while (p.firstChild) li.appendChild(p.firstChild);
        ul.appendChild(li);
        i++;
      }
      frag.appendChild(ul);
      changed = true;
    } else if (isNumberP(el)) {
      const ol = doc.createElement("ol");
      while (i < children.length && isNumberP(children[i])) {
        const p = children[i];
        stripPrefix(p, NUMBER_RE);
        const li = doc.createElement("li");
        while (p.firstChild) li.appendChild(p.firstChild);
        ol.appendChild(li);
        i++;
      }
      frag.appendChild(ol);
      changed = true;
    } else {
      frag.appendChild(el.cloneNode(true));
      i++;
    }
  }

  if (!changed) return null;
  const out = document.createElement("div");
  out.appendChild(frag);
  return out.innerHTML;
}

function ToolbarButton({ active, onClick, children, title }) {
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); onClick(); }}
      className={`h-7 w-7 flex items-center justify-center rounded transition-colors ${active ? "bg-[#0F4C81] text-white" : "text-slate-500 hover:bg-slate-100 hover:text-slate-700"}`}
      title={title}
    >
      {children}
    </button>
  );
}

function ColorPicker({ editor, onClose }) {
  const ref = useRef(null);
  const [recentColors, setRecentColors] = useState(getRecentColors);
  const [customColor, setCustomColor] = useState("#0F4C81");

  useEffect(() => {
    // Delay adding the listener to avoid the opening click from triggering close
    const timer = setTimeout(() => {
      const handleClickOutside = (e) => {
        if (ref.current && !ref.current.contains(e.target)) onClose();
      };
      document.addEventListener("mousedown", handleClickOutside);
      ref.current._cleanup = () => document.removeEventListener("mousedown", handleClickOutside);
    }, 50);
    return () => {
      clearTimeout(timer);
      if (ref.current?._cleanup) ref.current._cleanup();
    };
  }, [onClose]);

  const applyColor = (color) => {
    editor.chain().focus().setColor(color).run();
    saveRecentColor(color);
    setRecentColors(getRecentColors());
    onClose();
  };

  return (
    <div ref={ref} className="absolute top-8 left-0 z-50 bg-white rounded-lg shadow-xl border border-slate-200 p-3 w-[220px]" data-testid="color-picker">
      {/* Recent colors */}
      {recentColors.length > 0 && (
        <div className="mb-2">
          <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1.5">Recent</p>
          <div className="flex gap-1.5 flex-wrap">
            {recentColors.map((color) => (
              <button
                key={`recent-${color}`}
                type="button"
                className="w-7 h-7 rounded-md border-2 border-slate-200 hover:border-[#0F4C81] hover:scale-110 transition-all shadow-sm"
                style={{ backgroundColor: color }}
                onMouseDown={(e) => { e.preventDefault(); applyColor(color); }}
                title={color}
              />
            ))}
          </div>
        </div>
      )}
      {/* Preset grid */}
      <div className="mb-2">
        <p className="text-[10px] uppercase font-bold text-slate-400 tracking-wider mb-1.5">Colors</p>
        <div className="grid grid-cols-5 gap-1.5">
          {PRESET_COLORS.map((color) => (
            <button
              key={color}
              type="button"
              className="w-7 h-7 rounded-md border-2 border-transparent hover:border-[#0F4C81] hover:scale-110 transition-all shadow-sm"
              style={{ backgroundColor: color }}
              onMouseDown={(e) => { e.preventDefault(); applyColor(color); }}
              title={color}
            />
          ))}
        </div>
      </div>
      {/* Custom color */}
      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
        <input
          type="color"
          value={customColor}
          onChange={(e) => setCustomColor(e.target.value)}
          className="w-7 h-7 rounded cursor-pointer border-0 p-0"
        />
        <button
          type="button"
          className="text-xs font-medium text-[#0F4C81] hover:underline"
          onMouseDown={(e) => { e.preventDefault(); applyColor(customColor); }}
        >
          Apply Custom
        </button>
        <button
          type="button"
          className="text-xs text-slate-400 hover:text-slate-600 ml-auto"
          onMouseDown={(e) => { e.preventDefault(); editor.chain().focus().unsetColor().run(); onClose(); }}
        >
          Reset
        </button>
      </div>
    </div>
  );
}

function Toolbar({ editor }) {
  const [showColors, setShowColors] = useState(false);

  if (!editor) return null;

  return (
    <div className="flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-200 bg-slate-50/80 rounded-t-md flex-wrap">
      <ToolbarButton active={editor.isActive("bold")} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
        <Bold className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton active={editor.isActive("italic")} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
        <Italic className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton active={editor.isActive("underline")} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
        <UnderlineIcon className="w-3.5 h-3.5" />
      </ToolbarButton>

      <div className="w-px h-5 bg-slate-200 mx-1" />

      <ToolbarButton active={editor.isActive("bulletList")} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
        <List className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton active={editor.isActive("orderedList")} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List">
        <ListOrdered className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton
        active={false}
        onClick={() => {
          const current = editor.getHTML();
          const converted = autoBulletHtml(current);
          if (converted) {
            editor.commands.setContent(converted, true);
          } else {
            // no-op: nothing matched. Toast would need sonner import — keep silent.
          }
        }}
        title="Auto-format plain-text lists (*, -, •, 1.) into real bullets"
      >
        <Sparkles className="w-3.5 h-3.5" />
      </ToolbarButton>

      <div className="w-px h-5 bg-slate-200 mx-1" />

      <ToolbarButton active={false} onClick={() => editor.chain().focus().setHorizontalRule().run()} title="Line Break / Divider">
        <Minus className="w-3.5 h-3.5" />
      </ToolbarButton>

      <div className="w-px h-5 bg-slate-200 mx-1" />

      <ToolbarButton active={editor.isActive({ textAlign: "left" })} onClick={() => editor.chain().focus().setTextAlign("left").run()} title="Align Left">
        <AlignLeft className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton active={editor.isActive({ textAlign: "center" })} onClick={() => editor.chain().focus().setTextAlign("center").run()} title="Align Center">
        <AlignCenter className="w-3.5 h-3.5" />
      </ToolbarButton>
      <ToolbarButton active={editor.isActive({ textAlign: "right" })} onClick={() => editor.chain().focus().setTextAlign("right").run()} title="Align Right">
        <AlignRight className="w-3.5 h-3.5" />
      </ToolbarButton>

      <div className="w-px h-5 bg-slate-200 mx-1" />

      <div className="relative">
        <ToolbarButton active={showColors} onClick={() => setShowColors(!showColors)} title="Text Color">
          <Palette className="w-3.5 h-3.5" />
        </ToolbarButton>
        {showColors && <ColorPicker editor={editor} onClose={() => setShowColors(false)} />}
      </div>
    </div>
  );
}

function RichTextEditorImpl({ value, onChange, placeholder, rows = 3, testId }) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false,
        codeBlock: false,
        blockquote: false,
      }),
      Underline,
      TextStyle,
      Color,
      TextAlign.configure({ types: ["paragraph"] }),
    ],
    content: value || "",
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      if (html === "<p></p>" || html === "") {
        onChange("");
      } else {
        onChange(html);
      }
    },
    editorProps: {
      attributes: {
        class: `prose prose-sm max-w-none px-3 py-2 focus:outline-none text-sm text-slate-700`,
        style: `min-height: ${rows * 24}px`,
        "data-testid": testId || "rich-text-editor",
      },
    },
  }, []);

  const updateContent = useCallback(() => {
    if (editor && value !== undefined) {
      const currentHtml = editor.getHTML();
      if (value !== currentHtml && value !== (currentHtml === "<p></p>" ? "" : currentHtml)) {
        editor.commands.setContent(value || "");
      }
    }
  }, [editor, value]);

  useEffect(() => {
    updateContent();
  }, [updateContent]);

  return (
    <div className="border border-slate-200 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-[#0F4C81]/20 focus-within:border-[#0F4C81]">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
      {!value && placeholder && !editor?.isFocused && (
        <div className="absolute px-3 py-2 text-sm text-slate-400 pointer-events-none" style={{ marginTop: `-${rows * 24 + 8}px` }}>
          {placeholder}
        </div>
      )}
    </div>
  );
}

/**
 * Heuristic: does this HTML contain tags that TipTap will strip?
 * Used to auto-enable Advanced HTML mode when re-opening content with raw embeds.
 */
const RAW_HTML_TAGS = /<(iframe|table|thead|tbody|tr|td|th|video|audio|source|embed|object|script|style|form|input|button|select|figure|details|summary)\b/i;
function containsRawHtml(html) {
  return typeof html === "string" && RAW_HTML_TAGS.test(html);
}

/**
 * Public RichTextEditor with an Advanced HTML toggle.
 * - Rich mode: TipTap WYSIWYG (default)
 * - HTML mode: raw textarea that accepts iframes, tables, styled divs, scripts, etc.
 * Both modes read/write the same HTML string — no data-model change.
 * Auto-enables HTML mode when loaded content already has raw HTML tags.
 */
export function RichTextEditor({ value, onChange, placeholder, rows = 3, testId }) {
  const [htmlMode, setHtmlMode] = useState(() => containsRawHtml(value));

  // Auto-flip into HTML mode if the incoming value gains raw HTML tags (e.g., paste event)
  useEffect(() => {
    if (!htmlMode && containsRawHtml(value)) setHtmlMode(true);
  }, [value, htmlMode]);

  if (htmlMode) {
    return (
      <div className="border border-amber-200 rounded-md overflow-hidden bg-amber-50/30" data-testid={`${testId || "rich-text-editor"}-html-mode`}>
        <div className="flex items-center justify-between px-2 py-1.5 border-b border-amber-200 bg-amber-50">
          <div className="flex items-center gap-1.5 text-[11px] font-bold text-amber-700 uppercase tracking-wide">
            <Code2 className="w-3.5 h-3.5" /> Advanced HTML Mode
          </div>
          <button
            type="button"
            onClick={() => setHtmlMode(false)}
            className="text-[10px] uppercase font-semibold text-amber-700 hover:text-amber-900 px-2 py-0.5 rounded hover:bg-amber-100"
            data-testid={`${testId || "rich-text-editor"}-html-toggle-off`}
            title="Switch back to Rich Text. Any HTML that can't be represented in rich text (iframes, tables, scripts) may be stripped."
          >
            ← Rich Text
          </button>
        </div>
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || "Paste raw HTML here — iframes, tables, embeds, styled divs, etc."}
          className="w-full px-3 py-2 text-xs font-mono text-slate-700 bg-white focus:outline-none resize-y"
          style={{ minHeight: `${rows * 24}px` }}
          data-testid={`${testId || "rich-text-editor"}-html-textarea`}
          spellCheck={false}
        />
        <p className="px-3 py-1.5 text-[10px] text-amber-700/80 border-t border-amber-200 bg-amber-50/50">
          Rendered as-is on quote preview and customer view. Keep scripts to trusted sources only.
        </p>
      </div>
    );
  }

  return (
    <div className="relative">
      <RichTextEditorImpl value={value} onChange={onChange} placeholder={placeholder} rows={rows} testId={testId} />
      <button
        type="button"
        onClick={() => setHtmlMode(true)}
        className="absolute top-1.5 right-2 flex items-center gap-1 px-2 h-6 rounded text-[10px] font-semibold uppercase tracking-wide text-slate-500 hover:text-[#0F4C81] hover:bg-slate-100 transition-colors z-10"
        data-testid={`${testId || "rich-text-editor"}-html-toggle-on`}
        title="Advanced: paste raw HTML — iframes, tables, embeds, custom styles"
      >
        <Code2 className="w-3 h-3" /> HTML
      </button>
    </div>
  );
}

/**
 * Renders rich text HTML content safely.
 * Falls back to plain text with whitespace preservation if no HTML tags found.
 */
export function RichTextContent({ content, className = "" }) {
  if (!content) return null;

  // Handle {html: "..."} object format from TipTap
  const htmlStr = typeof content === "object" && content.html ? content.html : typeof content === "string" ? content : null;
  if (!htmlStr) return null;

  const hasHtml = /<[a-z][\s\S]*>/i.test(htmlStr);

  if (hasHtml) {
    return (
      <div
        className={`prose prose-sm max-w-none ${className}`}
        dangerouslySetInnerHTML={{ __html: htmlStr }}
      />
    );
  }

  return <p className={`whitespace-pre-wrap ${className}`}>{htmlStr}</p>;
}
